import React, { useEffect, useState } from "react";
import { GetRegisteredHGOs } from "../../../../Actions/AuthorizationActions";
import { CreateAccumulativeRequest } from "../../../../Actions/LedgerActions";
import { getCurrentDate, SuccessToast, WarningToast } from "../../../../Util";
import { FaPlus, FaTimes } from "react-icons/fa";
import { Spinner } from "react-bootstrap";
import "./AccumulativeRequest.css";

const AccumulativeMerchantRequestContainer = () => {
  const [values, setValues] = useState({
    voucherId: "",
    bankingFee: "",
    totalAmount: "",
    narration: "",
  });

  const [currency] = useState("SAR");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hgos, setHGOs] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  const [rows, setRows] = useState([
    {
      id: "",
      num: 0,
      amount: "",
    },
  ]);

  const addNewRow = () => {
    setRows([
      ...rows,
      {
        id: "",
        num: rows.length,
        amount: "",
      },
    ]);
  };

  const removeRow = (index) => {
    if (rows.length > 1) {
      const updatedRows = rows.filter((_, i) => i !== index);
      setRows(updatedRows);
      
      const total = updatedRows.reduce((sum, row) => {
        return sum + (parseFloat(row.amount) || 0);
      }, 0);
      setValues({ ...values, totalAmount: total });
    }
  };

  const handleCreate = () => {
    SuccessToast("Accumulative merchant request created successfully.");
    setValues({
      voucherId: "",
      bankingFee: "",
      totalAmount: "",
      narration: "",
    });
    setRows([
      {
        id: "",
        num: 0,
        amount: "",
      },
    ]);
    setValidationErrors({});
  };

  const handleSubmit = () => {
    const errors = {};

    if (!values.voucherId?.trim()) errors.voucherId = true;
    if (!values.narration?.trim()) errors.narration = true;
    if (values.bankingFee === "" || parseFloat(values.bankingFee) < 0) {
      errors.bankingFee = true;
    }

    rows.forEach((row) => {
      if (!row.id || row.id === "") {
        errors[`hgo_${row.num}`] = true;
      }
      if (!row.amount || parseFloat(row.amount) <= 0) {
        errors[`amount_${row.num}`] = true;
      }
    });

    setValidationErrors(errors);

    if (Object.keys(errors).length === 0) {
      const totalAmount = parseFloat(values.totalAmount) + parseFloat(values.bankingFee);
      const requestData = {
        voucherId: values.voucherId.trim(),
        totalAmount: totalAmount,
        bankingFee: parseFloat(values.bankingFee),
        narration: values.narration.trim(),
        currency: currency,
        date: new Date().toISOString().split('T')[0],
        users: rows.map((row) => ({
          id: row.id,
          amount: parseFloat(row.amount)
        }))
      };

      CreateAccumulativeRequest(
        requestData,
        setLoading,
        setError,
        handleCreate
      );
    } else {
      WarningToast("Please fill in all required fields.");
    }
  };


  const handleHGOChange = (index, value) => {
    const updatedRows = rows.map((r, i) => 
      i === index ? { ...r, id: value } : r
    );
    setRows(updatedRows);
    setValidationErrors({ ...validationErrors, [`hgo_${rows[index].num}`]: false });
  };

  const handleAmountChange = (index, value) => {
    const cleanValue = value.replace(/[^0-9.]/g, "").replace(/(\..*?)\..*/g, "$1").replace(/(\.\d{2})\d+/, "$1");
    
    const updatedRows = rows.map((r, i) => 
      i === index ? { ...r, amount: cleanValue } : r
    );
    setRows(updatedRows);
    
    const total = updatedRows.reduce((sum, r) => {
      return sum + (parseFloat(r.amount) || 0);
    }, 0);
    setValues({ ...values, totalAmount: total });
    setValidationErrors({ ...validationErrors, [`amount_${rows[index].num}`]: false });
  };

  const handleBankingFeeChange = (value) => {
    const cleanValue = value.replace(/[^0-9.]/g, "").replace(/(\..*?)\..*/g, "$1").replace(/(\.\d{2})\d+/, "$1");
    setValues({ ...values, bankingFee: cleanValue });
    setValidationErrors({ ...validationErrors, bankingFee: false });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
    setValidationErrors({ ...validationErrors, [name]: false });
  };

  const loadHGOs = async () => {
    GetRegisteredHGOs(setHGOs, setError);
  };

  useEffect(() => {
    loadHGOs();
  }, []);

  return (
    <div className="container-fluid main-content-table">
      <div className="row">
        <div className="col-12">
          <div className="bg-light p-2 rounded">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <h4 className="mb-0">Accumulative Merchant Request</h4>
              </div>
              <div className="d-flex flex-column">
                <label className="form-label small">Date</label>
                <input 
                  type="text" 
                  className="form-control date-input" 
                  value={getCurrentDate()} 
                  readOnly 
                />
              </div>
            </div>

            <div className="card">
              <div className="card-body p-2">
                <div className="row mb-2">
                  <div className="col-md-6">
                    <label className="form-label small">Voucher ID *</label>
                    <input
                      type="text"
                      name="voucherId"
                      className={`form-control ${validationErrors.voucherId ? 'is-invalid' : ''}`}
                      placeholder="000000"
                      value={values.voucherId}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Currency *</label>
                    <input
                      type="text"
                      className="form-control"
                      value="SAR"
                      readOnly
                    />
                  </div>
                </div>

                {rows.map((row, index) => (
                  <div key={index} className="row mb-2">
                    <div className="col-12">
                      <div className="p-2 rounded transfer-row">
                        <div className="row">
                          <div className="col-md-5">
                            <label className="form-label small">HGO Company *</label>
                            <select
                              className={`form-select ${validationErrors[`hgo_${row.num}`] ? 'is-invalid' : ''}`}
                              value={row.id}
                              onChange={(e) => handleHGOChange(index, e.target.value)}
                            >
                              <option value="">Search</option>
                              {hgos
                                .filter((hgo) => {
                                  return !rows.some((r, i) => i !== index && r.id === hgo.id);
                                })
                                .map((hgo) => (
                                  <option key={hgo.id} value={hgo.id}>
                                    {hgo.name}
                                  </option>
                                ))}
                            </select>
                          </div>
                          <div className="col-md-5">
                            <label className="form-label small">Amount *</label>
                            <input
                              type="text"
                              className={`form-control ${validationErrors[`amount_${row.num}`] ? 'is-invalid' : ''}`}
                              placeholder="20,555"
                              value={row.amount ? Number(row.amount).toLocaleString() : ""}
                              onChange={(e) => handleAmountChange(index, e.target.value)}
                            />
                          </div>
                          <div className="col-md-2 d-flex align-items-end">
                            {rows.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm remove-btn"
                                onClick={() => removeRow(index)}
                              >
                                <FaTimes className="me-1" />
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="row mb-2">
                  <div className="col-12">
                    <button
                      type="button"
                      className="btn btn-link text-success p-0 add-button"
                      onClick={addNewRow}
                    >
                      <FaPlus className="me-1" />
                      Add
                    </button>
                  </div>
                </div>

                <div className="row mb-2">
                  <div className="col-md-6">
                    <label className="form-label small">Banking Fee *</label>
                    <input
                      type="text"
                      className={`form-control ${validationErrors.bankingFee ? 'is-invalid' : ''}`}
                      placeholder="2,000"
                      value={values.bankingFee ? Number(values.bankingFee).toLocaleString() : ""}
                      onChange={(e) => handleBankingFeeChange(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Total *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={values.totalAmount ? Number(values.totalAmount).toLocaleString() : ""}
                      readOnly
                    />
                  </div>
                </div>

                <div className="row mb-2">
                  <div className="col-12">
                    <label className="form-label small">Narration *</label>
                    <textarea
                      name="narration"
                      className={`form-control ${validationErrors.narration ? 'is-invalid' : ''}`}
                      rows="3"
                      placeholder="Comment Here"
                      value={values.narration}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    className="btn btn-success btn-lg px-5"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                      {loading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        "Submit"
                      )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccumulativeMerchantRequestContainer;

